/* eslint-disable no-console */
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button, Label, LABEL_TYPE } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import { UPLOAD_PICKER_TYPES } from '@/constants';
import { AuthContext, ProfilePictureContext } from '@/contexts';
import { PERMISSION_TYPE, PRIVATE_PAGE_ROUTES } from '@/enums';
import { BlobLocation } from '@/enums/BlobLocationEnum';
import {
  convertHeicToJpeg,
  getAssetExtension,
  getValidationFormatsFromPickerTypes,
  isHeicFileContent,
  validateUploadFile
} from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { useIsUploaderSidebarShown, useUploadFilePicker } from '@/hooks';
import { MfaSettings } from '@/pages/ProfilePage/MfaSettings';
import { NewPassword } from '@/pages/ProfilePage/NewPassword';
import { PersonalDetails } from '@/pages/ProfilePage/PersonalDetails';
import {
  getBlobClient,
  getPictureUrl,
  uploadPicture
} from '@/pages/ProfilePage/ProfilePictureUploadHelper';
import { UserAvatar } from '@/pages/ProfilePage/UserAvatar';
import {
  LeftChevronIcon as ArrowLeftIcon,
  LogOut as LogOutIcon,
  UploadPicture as PictureUpload,
  SentoaLogo
} from '@/resources';
import {
  useGetCurrentUserQuery,
  useSasTokenProfilePicture,
  useUploadProfilePictureMutation
} from '@/services';
import { ErrorResponse, SasTokenPayload, UploadProfilPicture } from '@/types';

export const ProfilePage = () => {
  const { data: user } = useGetCurrentUserQuery(true);
  const navigate = useNavigate();
  const [t] = useTranslation('common');

  const { profilePictureUrl } = useContext(ProfilePictureContext);
  const [isUploadingProfilePicture, setIsUploadingProfilePicture] = useState(false);

  const { logout, permissions } = useContext(AuthContext);
  const hasOrgAccess = permissions.includes(PERMISSION_TYPE.ORG_ACCESS);
  const canControl =
    permissions.includes(PERMISSION_TYPE.CONTROL_ASSETS) &&
    permissions.includes(PERMISSION_TYPE.CONTROL_STREAM);
  const canViewText = permissions.includes(PERMISSION_TYPE.VIEW_TEXT);
  const isSidebarShown = useIsUploaderSidebarShown();

  const profilePicturePickerTypes = UPLOAD_PICKER_TYPES.IMAGE;

  const { mutate: uploadProfilePicture } = useUploadProfilePictureMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('profilePictureSuccess'));
    },
    onError: () => {
      showToast(ToastType.ERROR, t('profilePictureError'));
    }
  });

  const { mutateAsync: sasTokenProfilePicture } = useSasTokenProfilePicture(queryClient, {
    onSuccess: () => {
      console.log(t('sasTokenSuccessfullyCreated'));
    },
    onError: () => {
      showToast(ToastType.ERROR, t('sasError'));
    }
  });

  const uploadSelectedProfilePicture = async (file: File) => {
    const sasToken: SasTokenPayload = {
      id: user?.data.id,
      isPublic: false,
      isThumbnail: false,
      assetName: '',
      extension: getAssetExtension(file.name),
      blobLocation: BlobLocation.USERS
    };

    const data = await sasTokenProfilePicture(sasToken);

    if (!data) {
      return;
    }

    const assetBlobClient = getBlobClient(data);

    if (!(await uploadPicture(assetBlobClient, file))) {
      return;
    }

    const newProfilePictureUrl = getPictureUrl(data);

    const payload: UploadProfilPicture = {
      id: user?.data.id,
      profilePictureUrl: newProfilePictureUrl
    };

    try {
      uploadProfilePicture(payload);
    } catch (error) {
      showToast(
        ToastType.ERROR,
        t(`${(error as ErrorResponse).response.data.ErrorMessages[0].DisplayMessage}`)
      );
    }
  };

  const profilePictureValidationFormats = useMemo(
    () => getValidationFormatsFromPickerTypes(profilePicturePickerTypes),
    [profilePicturePickerTypes]
  );

  const handleProfilePictureFilesSelected = async (files: File[]) => {
    const file = files[0];

    if (!file) {
      return;
    }
    let fileToUpload = file;

    const isHeic = await isHeicFileContent(file);

    if (isHeic) {
      try {
        fileToUpload = await convertHeicToJpeg(file);
      } catch {
        showToast(ToastType.ERROR, t('unsupportedMediaFormat'));
        return;
      }
    }

    const validationResult = validateUploadFile({
      file: fileToUpload,
      validationFormats: profilePictureValidationFormats
    });

    if (validationResult.errorType) {
      showToast(ToastType.ERROR, t('unsupportedMediaFormat'));
      return;
    }

    try {
      setIsUploadingProfilePicture(true);
      await uploadSelectedProfilePicture(fileToUpload);
    } catch (error) {
      showToast(
        ToastType.ERROR,
        t(`${(error as ErrorResponse).response.data.ErrorMessages[0].DisplayMessage}`)
      );
    } finally {
      setIsUploadingProfilePicture(false);
    }
  };

  const { openFilePicker: openProfilePicturePicker, hiddenInputProps } = useUploadFilePicker({
    pickerTypes: profilePicturePickerTypes,
    multiple: false,
    disabled: isUploadingProfilePicture,
    onFilesSelected: handleProfilePictureFilesSelected
  });

  return (
    <>
      <input {...hiddenInputProps} />
      <div className={`${!hasOrgAccess && 'md:flex md:size-full md:flex-col'}`}>
        <div className='overflow-y-scroll scrollbar-none md:h-screen md:overflow-y-auto'>
          {!hasOrgAccess && !isSidebarShown && (
            <div className='absolute top-0 z-20 flex h-[45px] w-full items-center justify-between bg-primary-gray px-4 md:relative md:h-11 md:justify-center md:px-0'>
              <Button
                variant={ButtonVariant.SECONDARY}
                handleOnClick={() =>
                  canControl || canViewText
                    ? navigate(PRIVATE_PAGE_ROUTES.DEVICES)
                    : navigate('/upload-content/requests')
                }
                className='border-0 !px-0 md:absolute md:left-0 md:!px-3'>
                <ArrowLeftIcon className='text-base text-primary-blue' />
              </Button>
              <SentoaLogo className='h-[26px] w-[108px] md:absolute md:justify-center' />
              <LogOutIcon
                className='right-3 mr-0 flex cursor-pointer text-2xl text-primary-blue md:absolute md:mr-auto md:block md:text-3xl'
                onClick={logout}
              />
            </div>
          )}
          <div
            className={`md:flex md:size-full md:items-center ${hasOrgAccess ? 'md:flex-col' : 'md:mt-5 md:justify-center'}`}>
            <div
              className={`px-3 md:flex md:w-full md:flex-col ${hasOrgAccess ? 'md:space-y-8 md:px-10 md:pb-10' : 'md:h-[calc(100vh-80px)] md:max-w-[960px] md:px-0'}`}>
              <div className={`${hasOrgAccess && 'md:mt-8'}`}>
                <Label
                  text={t('profile')}
                  type={hasOrgAccess ? LABEL_TYPE.H1 : LABEL_TYPE.H2}
                  className={`hidden md:block ${!hasOrgAccess && 'mb-2 w-full pb-4 text-2xl'}`}
                />
                <Label
                  text={t('profile')}
                  type={LABEL_TYPE.H2}
                  className='mt-5 w-full pb-4 text-2xl md:hidden'
                />
              </div>
              <div
                className={`flex h-20 w-auto items-center rounded-lg bg-indigo-50 sm:flex-col md:h-24 md:w-full md:flex-row ${hasOrgAccess ? 'sm:w-full' : 'sm:w-[960px] md:mt-1'}`}>
                <div className='relative md:ml-3 md:mr-1'>
                  <label className='cursor-pointer md:relative md:cursor-auto'>
                    {profilePictureUrl ? (
                      <div>
                        <img
                          src={profilePictureUrl}
                          alt={t('profilePicture')}
                          className='mr-2 size-14 rounded-full object-cover sm:w-16 md:h-16'
                        />
                        <div className='absolute bottom-0 right-0'>
                          <PictureUpload
                            onClick={openProfilePicturePicker}
                            className='size-6 cursor-pointer sm:w-6'
                          />
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className={`${!hasOrgAccess && 'ml-3'}`}>
                          <UserAvatar style='w-14 md:w-16 h-14 md:h-16 mr-2 text-lg md:text-xl bg-primary-white border border-accent-light-gray text-primary-blue' />
                        </div>
                        <div className='absolute bottom-0 right-0'>
                          <PictureUpload
                            onClick={openProfilePicturePicker}
                            className='cursor-pointer sm:w-6 md:h-6'
                          />
                        </div>
                      </div>
                    )}
                  </label>
                </div>
                <div className='ml-1'>
                  <Label
                    text={user?.data.firstName + ' ' + user?.data.lastName}
                    type={hasOrgAccess ? LABEL_TYPE.H2 : LABEL_TYPE.H3}
                    className='hidden font-bold text-primary-blue md:block'
                  />
                  <Label
                    text={user?.data.firstName + ' ' + user?.data.lastName}
                    type={LABEL_TYPE.H4}
                    className='font-bold text-primary-blue md:hidden'
                  />
                  <Label className='truncate' text={user?.data.email} />
                </div>
              </div>
              <div
                className={`hidden grid-cols-2 px-1 md:grid ${hasOrgAccess ? 'w-full gap-x-12 pt-2 sm:w-full' : 'gap-x-10 pt-10'}`}>
                <PersonalDetails />
                <NewPassword />
              </div>
              <div className='hidden px-1 md:block'>
                <MfaSettings />
              </div>
              <div className='grid pt-6 md:hidden'>
                <div>
                  <PersonalDetails />
                </div>
                <div className='flex justify-center'>
                  <div className='mt-5 h-px w-full bg-primary-black opacity-30' />
                </div>
                <div className='mt-4'>
                  <NewPassword />
                </div>
                <div className='flex justify-center'>
                  <div className='mt-5 h-px w-full bg-primary-black opacity-30' />
                </div>
                <div className='my-6'>
                  <MfaSettings />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
